import React, { useState, useMemo } from "react"
import { Box, Collapse, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from "@mui/material"
import { differenceInDays, parseISO, format } from "date-fns"
import { getExportConfig } from "../../component/config/exportConfig"
import { currencyFormat } from "../../utils/number"
import { LoadingButton } from "@mui/lab"
import { formatPhone } from "../../utils/string"
import { useExport } from "../../hooks/useExport"
import { Card } from "@tremor/react"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import GenericTablePaginationActions from "../../component/generic/GenericTablePaginationActions"

const columnsByStore = [
    { id: 'storeName', label: 'Nome da Loja', minWidth: 150, align: 'left' },
    { id: 'averageLongevity', label: 'Média de Longevidade (dias)', minWidth: 80, align: 'center' },
    { id: 'value', label: 'Valor', minWidth: 100, align: 'center' },
    { id: 'orderQuantity', label: 'Qtd. Pedidos', minWidth: 100, align: 'center' },
    { id: 'averageTicket', label: 'Ticket Médio', minWidth: 100, align: 'center' },
    { id: 'customer', label: 'Clientes Únicos', minWidth: 100, align: 'center' },
    { id: 'expandDetails', label: '', minWidth: 50, align: 'left' },
]

const columnsByCustomer = [
    { id: 'customer', label: 'Cliente', minWidth: 150, align: 'left' },
    { id: 'phone', label: 'Telefone', minWidth: 100, align: 'center' },
    { id: 'startDate', label: 'Cliente Desde', minWidth: 100, align: 'center' },
    { id: 'longevity', label: 'Longevidade (dias)', minWidth: 100, align: 'center' },
    { id: 'totalValue', label: 'Valor Total', minWidth: 100, align: 'center' },
    { id: 'order', label: 'Pedido(s)', minWidth: 80, align: 'right' },
    { id: 'averageTicket', label: 'Ticket Médio', minWidth: 100, align: 'right' },
]

function Row({ row, allSalesData }) {
    const { handleExport } = useExport()
    const [open, setOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const handleChangePage = (_, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const customerDetails = useMemo(() => {
        const storeSales = allSalesData.filter(sale => sale.idUnidade === row.id)

        const groupedCustomers = storeSales.reduce((acc, curr) => {
            const idPessoa = curr.idPessoa

            if (!acc[idPessoa]) {
                acc[idPessoa] = {
                    idPessoa,
                    nomeCliente: curr.nomeCliente || 'Cliente Final',
                    telefoneCliente: curr.telefoneCliente || '-',
                    createdAt: curr.createdAt,
                    totalValue: 0,
                    orderQuantity: 0,
                }
            }

            acc[idPessoa].totalValue += parseFloat(curr.valorTotal || 0)
            acc[idPessoa].orderQuantity += 1

            return acc
        }, {})

        return Object.values(groupedCustomers).map(c => ({
            ...c,
            longevity: c.createdAt ? differenceInDays(new Date(), parseISO(c.createdAt)) : 0,
            formattedDate: c.createdAt ? format(parseISO(c.createdAt), 'dd/MM/yyyy') : '-',
            averageTicket: c.totalValue / c.orderQuantity
        })).sort((a, b) => b.totalValue - a.totalValue)

    }, [row.id, allSalesData])

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - customerDetails?.length) : 0

    const handleExportCustomerData = () => {
        const option = getExportConfig('/customerDetails')

        handleExport(customerDetails, option, 'detalhamento-por-cliente')
    }

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell align="left">{row.storeName}</TableCell>
                <TableCell align="center">{row.averageLongevity}</TableCell>
                <TableCell align="center">{currencyFormat(row.value)}</TableCell>
                <TableCell align="center">{row.orderQuantity}</TableCell>
                <TableCell align="center">{currencyFormat(row.averageTicket)}</TableCell>
                <TableCell align="center">{row.customer}</TableCell>
                <TableCell align="center">
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, padding: 1, borderRadius: 2 }}>
                            <Stack direction={'row'} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                    Detalhamento por Cliente - {row.storeName}
                                </Typography>
                                <LoadingButton onClick={handleExportCustomerData}>
                                    Exportar Dados
                                </LoadingButton>
                            </Stack>

                            <TableContainer component={Paper} elevation={1}>
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            {columnsByCustomer.map((col) => (
                                                <TableCell key={col.id} align={col.align} sx={{ fontWeight: 'bold' }}>
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {(rowsPerPage > 0 ? customerDetails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : customerDetails).map((customerRow) => (
                                            <TableRow key={customerRow.idPessoa}>
                                                <TableCell component="th" scope="row">{customerRow.nomeCliente}</TableCell>
                                                <TableCell align="center">{formatPhone(customerRow.telefoneCliente)}</TableCell>
                                                <TableCell align="center">{customerRow.formattedDate}</TableCell>
                                                <TableCell align="center">{customerRow.longevity}</TableCell>
                                                <TableCell align="center">{currencyFormat(customerRow.totalValue)}</TableCell>
                                                <TableCell align="right">{customerRow.orderQuantity}</TableCell>
                                                <TableCell align="right">{currencyFormat(customerRow.averageTicket)}</TableCell>
                                            </TableRow>
                                        ))}

                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: 33 * emptyRows }}>
                                                <TableCell colSpan={7} />
                                            </TableRow>
                                        )}
                                    </TableBody>

                                    <TableFooter>
                                        <TableRow>
                                            <TablePagination
                                                rowsPerPageOptions={[10, 25, 50, 100, 200, { label: 'Todos', value: -1 }]}
                                                colSpan={7}
                                                count={customerDetails?.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                labelRowsPerPage='linhas por página'
                                                slotProps={{
                                                    select: {
                                                        inputProps: {
                                                            'aria-label': 'linhas por página',
                                                        },
                                                    },
                                                    native: true,
                                                }}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                ActionsComponent={GenericTablePaginationActions}
                                            />
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

export default function CustomerTable({ currentMonthAllCustomersSales }) {

    const rows = useMemo(() => {
        if (!currentMonthAllCustomersSales || currentMonthAllCustomersSales.length === 0) return []

        const groupedData = currentMonthAllCustomersSales.reduce((acc, curr) => {
            const idUnidade = curr.idUnidade

            if (!acc[idUnidade]) {
                acc[idUnidade] = {
                    idUnidade: curr.idUnidade,
                    storeName: curr.unidadeName,
                    totalValue: 0,
                    orderQuantity: 0,
                    customersSet: new Set(),
                    totalLongevityDays: 0,
                    countLongevity: 0
                }
            }

            acc[idUnidade].totalValue += parseFloat(curr.valorTotal || 0)
            acc[idUnidade].orderQuantity += 1
            acc[idUnidade].customersSet.add(curr.idPessoa)

            if (curr.createdAt) {
                const days = differenceInDays(new Date(), parseISO(curr.createdAt))
                acc[idUnidade].totalLongevityDays += days
                acc[idUnidade].countLongevity += 1
            }

            return acc
        }, {})

        return Object.values(groupedData).map(unit => ({
            id: unit.idUnidade,
            storeName: unit.storeName,
            value: unit.totalValue,
            orderQuantity: unit.orderQuantity,
            customer: unit.customersSet.size,
            averageTicket: unit.totalValue / unit.orderQuantity,
            averageLongevity: unit.countLongevity > 0 ? Math.round(unit.totalLongevityDays / unit.countLongevity) : 0
        })).sort((a, b) => b.value - a.value)

    }, [currentMonthAllCustomersSales])

    return (
        <Card>
            <Stack sx={{ display: 'flex' }}>
                <Typography variant="h4">
                    Clientes com Pedidos no Período
                </Typography>
            </Stack>

            <TableContainer elevation={0} component={Paper} sx={{ marginTop: 2 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columnsByStore.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    style={{ minWidth: column.minWidth, fontSize: '14px', fontWeight: 'bold' }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row) => (
                            <Row
                                key={row.id}
                                row={row}
                                allSalesData={currentMonthAllCustomersSales}
                            />
                        ))}

                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Nenhum dado encontrado para o período.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    )
}